import { NoResultComponent } from '@/app/components/shared/no-result/no-result.component';
import { SkeletonMatchLineupsComponent } from '@/app/components/shared/skeleton';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { MatchLineupsService } from '../../../../services';
import { Missing } from '../../../../models';
import { TranslationService } from '@/app/services/translation.service';
import {
  LineupPlayersGroupComponent,
  LineupCoachsGroupComponent,
} from '../../../../components';

@Component({
  selector: 'app-lineup-new',
  standalone: true,
  imports: [
    TranslateModule,
    SkeletonMatchLineupsComponent,
    NoResultComponent,
    CommonModule,
    LineupPlayersGroupComponent,
    LineupCoachsGroupComponent,
  ],
  templateUrl: './lineup.component.html',
  styleUrl: './lineup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineupComponent {
  private activeRoute = inject(ActivatedRoute);
  private translationService = inject(TranslationService);
  public lineupsService = inject(MatchLineupsService);

  stadiumImg = environment.urlSVG + 'stadium.svg';
  stadiumVerticalImg = environment.urlSVG + 'stadium-vertical.svg';

  awayPlayersInTheField = computed(() => {
    if (this.lineupsService.matchLineups() === null) return [];

    const awayPlayer =
      this.lineupsService.matchLineups()!.AwayLineup.Starting ?? [];
    // const homePlayer =
    //   this.lineupsService.matchLineups()!.HomeLineup.Starting ?? [];
    return [...awayPlayer];
    // return {
    //   awayPlayer,
    //   homePlayer,
    // }
  });

  isMobile = signal<boolean>(window.innerWidth <= 450);

  homePlayersInTheField = computed(() => {
    if (this.lineupsService.matchLineups() === null) return [];

    // const awayPlayer =
    //   this.lineupsService.matchLineups()!.AwayLineup.Starting ?? [];
    const homePlayer =
      this.lineupsService.matchLineups()!.HomeLineup.Starting ?? [];
    return [...homePlayer];
    // return {
    //   awayPlayer,
    //   homePlayer,
    // }
  });

  // v1 1 init
  tsExternalMatchId = input.required<string>();
  sport: string = 'Futbol';
  params = { id: '' };
  // v1 end

  matchId = signal<string | null>(null);

  constructor() {
    window.addEventListener('resize', () => {
      this.isMobile.set(window.innerWidth <= 500);
    });

    this.activeRoute.paramMap.subscribe((params) => {
      this.matchId.set(params.get('matchId'));
    });

    if (this.matchId()) {
      this.lineupsService.getMatchLineups(this.matchId()!);
    }
  }

  getPlayerPosition(player: Missing, isHome: boolean) {
    const isMobile = window.innerWidth <= 500;

    if (isMobile) {
      // 📱 Mobile → cancha vertical
      return {
        left: player.XCoordinate ? player.XCoordinate / 1.2 + '%' : '0%',
        top: isHome
          ? player.YCoordinate
            ? player.YCoordinate / 2 - 7 + '%'
            : '0%'
          : 'auto',
        bottom: !isHome
          ? player.YCoordinate
            ? player.YCoordinate / 2 + 2 + '%'
            : '0%'
          : 'auto',
        transform: 'translate(50%, 50%)',
      };
    } else {
      // 💻 Desktop → cancha horizontal
      return {
        left: isHome
          ? player.YCoordinate
            ? player.YCoordinate / 2 - 5 + '%'
            : '0%'
          : 'auto',
        right: !isHome
          ? player.YCoordinate
            ? player.YCoordinate / 2 + '%'
            : '0%'
          : 'auto',
        top: player.XCoordinate ? player.XCoordinate / 1.2 + '%' : '0%',
        transform: 'translate(50%, 50%)',
      };
    }
  }

  getImagePlayer(image?: string): string {
    if (!image || image.trim().length === 0) {
      return environment.urlIMG + 'default.png';
    }

    return image;
  }

  getShortName(player: Missing) {
    const { ShortNameTranslation, ShortName, NameTranslation, Name } = player;

    const traduccionByApi = ShortNameTranslation ?? NameTranslation ?? null;

    const defaultName =
      ShortName?.trim() || Name?.trim() || 'Jugador desconocido';

    return this.translationService.getTraduccionName(
      traduccionByApi,
      defaultName
    );
  }

  hasData() {
    return (
      this.hasStartingPlayers() ||
      this.hasSubstitutePlayers() ||
      this.hasMissingPlayers() ||
      this.hasCoaches()
    );
  }

  hasStartingPlayers() {
    if (this.lineupsService.matchLineups() === null) return false;

    const homeStartingPlayers =
      this.lineupsService.matchLineups()!.HomeLineup.Starting !== null &&
      this.lineupsService.matchLineups()!.HomeLineup.Starting!.length > 0;
    const awayStartingPlayers =
      this.lineupsService.matchLineups()!.AwayLineup.Starting !== null &&
      this.lineupsService.matchLineups()!.AwayLineup.Starting!.length > 0;

    return homeStartingPlayers || awayStartingPlayers;
  }

  hasSubstitutePlayers() {
    if (this.lineupsService.matchLineups() === null) return false;

    const homeSubstitutesPlayers =
      this.lineupsService.matchLineups()!.HomeLineup.Substitutes !== null &&
      this.lineupsService.matchLineups()!.HomeLineup.Substitutes!.length > 0;
    const awaySubstitutesPlayers =
      this.lineupsService.matchLineups()!.AwayLineup.Substitutes !== null &&
      this.lineupsService.matchLineups()!.AwayLineup.Substitutes!.length > 0;

    return homeSubstitutesPlayers || awaySubstitutesPlayers;
  }

  hasMissingPlayers() {
    if (this.lineupsService.matchLineups() === null) return false;

    const homeMissingPlayers =
      this.lineupsService.matchLineups()!.HomeLineup.Missing !== null &&
      this.lineupsService.matchLineups()!.HomeLineup.Missing!.length > 0;
    const awayMissingPlayers =
      this.lineupsService.matchLineups()!.AwayLineup.Missing !== null &&
      this.lineupsService.matchLineups()!.AwayLineup.Missing!.length > 0;

    return homeMissingPlayers || awayMissingPlayers;
  }

  hasCoaches() {
    return (
      this.lineupsService.matchLineups() &&
      (this.lineupsService.matchLineups()!.HomeLineup.Coach ||
        this.lineupsService.matchLineups()!.AwayLineup.Coach)
    );
  }
}
